import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, Crown, Gift, Star, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { BRAND } from "@/brand.config";

const heroImg = "https://images.unsplash.com/photo-1567958451986-2de427a4a0be?auto=format&fit=crop&w=1920&q=85";
const fabric = "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=1400&q=85";
const editorial = "https://images.unsplash.com/photo-1556740738-b6a63e27c4df?auto=format&fit=crop&w=1400&q=85";
const boutique = "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=1400&q=85";

const products = [
  { n: "01", name: "Fundle Loyalty", tag: "Enterprise loyalty, CRM and engagement.", body: "The institutional loyalty + CRM platform powering retailers, brands, malls, banks, hospitality, healthcare and airlines." },
  { n: "02", name: "Fundle Brain", tag: "Ask your customer data anything.", body: "The AI loyalty and customer intelligence engine — an AI analyst, strategist, campaign manager and consultant in one." },
  { n: "03", name: "Fundle Experiences", tag: "India's shopping rewards ecosystem.", body: "The consumer-facing rewards marketplace. 270+ premium brands, 10-second delivery, redeemable via WhatsApp, web and app." },
  { n: "04", name: "Fundle Reach", tag: "The marketplace for India's malls.", body: "Retail media + Space-on-Hire marketplace. 3,759 live ad spaces across 123+ premium malls and 47+ Indian cities." },
  { n: "05", name: "ADSR", tag: "Mall analytics and retail intelligence.", body: "The operating system for shopping malls — sales, footfall, tenant performance, occupancy and benchmarking in one institutional dashboard." },
  { n: "06", name: "ConsentFirst", tag: "DPDP compliance, audit-ready.", body: "India's leading Consent Management Platform. DPDP Act compliance in 30 days. 500+ organisations, 10M+ consents managed." },
];

const tiers = [
  { name: "Silver", minSpend: "₹0", multiplier: "1x", birthday: "200 pts", colorClass: "pill-silver" },
  { name: "Gold", minSpend: "₹25,000", multiplier: "1.25x", birthday: "500 pts", colorClass: "pill-gold" },
  { name: "Platinum", minSpend: "₹75,000", multiplier: "1.5x", birthday: "1000 pts", colorClass: "pill-platinum" },
  { name: "Diamond", minSpend: "₹1,50,000", multiplier: "2x", birthday: "2000 pts", colorClass: "pill-diamond" },
];

export default function Home() {
  const [form, setForm] = useState({ name: "", mobile: "", email: "", city: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [cms, setCms] = useState(null);

  useEffect(() => {
    document.title = BRAND.meta.title;
    api.get("/cms/content").then((r) => setCms(r.data)).catch(() => {});
    const m = document.querySelector('meta[name="description"]');
    const desc = BRAND.meta.description;
    if (m) m.setAttribute("content", desc);
    else {
      const meta = document.createElement("meta");
      meta.name = "description";
      meta.content = desc;
      document.head.appendChild(meta);
    }
  }, []);

  const c = cms?.home || {};

  const submit = async (e) => {
    e.preventDefault();
    if (!form.mobile || form.mobile.length < 10) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/public/register-interest", form);
      setResult(r.data);
      if (r.data.already_registered) {
        toast.success(`Welcome back! You're a ${r.data.tier?.toUpperCase()} member with ${r.data.points} points.`);
      } else {
        toast.success(BRAND.welcomeToast);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative kazo-bg-black text-white overflow-hidden">
        <div className="grid lg:grid-cols-2 min-h-[80vh]">
          <div className="relative order-2 lg:order-1 px-6 lg:px-16 py-16 lg:py-24 flex flex-col justify-center">
            <div className="text-[11px] uppercase tracking-[0.3em] text-white/50 mb-6 fade-up">{c.hero_eyebrow || BRAND.homeCopy.heroEyebrow}</div>
            <h1 className="editorial-headline text-5xl md:text-6xl lg:text-7xl xl:text-8xl mb-6 fade-up" style={{ animationDelay: "0.1s" }}>
              {c.hero_headline_1 || "Where style"}<br />
              <em className="kazo-text-champagne font-light">{c.hero_headline_em || "earns"}</em> {c.hero_headline_2?.split(" ")[0] || "you"}<br />
              {c.hero_headline_2?.split(" ").slice(1).join(" ") || "more."}
            </h1>
            <p className="text-white/70 text-lg max-w-md mb-10 leading-relaxed fade-up" style={{ animationDelay: "0.2s" }}>
              {c.hero_subtext || BRAND.homeCopy.heroSubtext}
            </p>

            <form onSubmit={submit} className="space-y-3 max-w-md fade-up" style={{ animationDelay: "0.3s" }}>
              <div className="grid grid-cols-2 gap-3">
                <input style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.2)",color:"#fff"}} className="px-3 py-2.5 text-sm placeholder:text-white/40 rounded-[2px] focus:outline-none focus:border-white/60" placeholder="Full name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} data-testid="hero-input-name" />
                <input style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.2)",color:"#fff"}} className="px-3 py-2.5 text-sm placeholder:text-white/40 rounded-[2px] focus:outline-none focus:border-white/60" placeholder="Mobile number" value={form.mobile} onChange={(e)=>setForm({...form,mobile:e.target.value})} data-testid="hero-input-mobile" />
              </div>
              <input style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.2)",color:"#fff"}} className="w-full px-3 py-2.5 text-sm placeholder:text-white/40 rounded-[2px] focus:outline-none focus:border-white/60" placeholder="Email (optional)" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} data-testid="hero-input-email" />
              <input style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.2)",color:"#fff"}} className="w-full px-3 py-2.5 text-sm placeholder:text-white/40 rounded-[2px] focus:outline-none focus:border-white/60" placeholder="City" value={form.city} onChange={(e)=>setForm({...form,city:e.target.value})} data-testid="hero-input-city" />
              <button type="submit" disabled={submitting} className="k-btn kazo-bg-champagne text-black w-full justify-center font-semibold text-sm uppercase tracking-widest" data-testid="hero-submit-btn">
                {submitting ? "Joining…" : BRAND.ctaJoinFree} <ArrowUpRight className="w-4 h-4" />
              </button>
            </form>
            {result && (
              <div className="mt-4 text-sm text-champagne kazo-text-champagne fade-up">
                {result.already_registered ? `Welcome back. Tier: ${result.tier?.toUpperCase()} · Balance: ${result.points} pts` : `You're in! +${result.welcome_bonus} welcome points credited.`}
              </div>
            )}

            <div className="mt-10 flex items-center gap-8 text-xs text-white/40 fade-up" style={{ animationDelay: "0.4s" }}>
              <div><span className="text-2xl font-display text-white">{c.stats_members || "1.5L+"}</span><div className="uppercase tracking-widest mt-1">Members</div></div>
              <div className="h-8 w-px bg-white/20" />
              <div><span className="text-2xl font-display text-white">{c.stats_cities || "15+"}</span><div className="uppercase tracking-widest mt-1">Cities</div></div>
              <div className="h-8 w-px bg-white/20" />
              <div><span className="text-2xl font-display text-white">{c.stats_stores || "25+"}</span><div className="uppercase tracking-widest mt-1">Stores</div></div>
            </div>
          </div>

          <div className="relative order-1 lg:order-2 min-h-[50vh] lg:min-h-0 overflow-hidden">
            <img src={c.hero_image_url || heroImg} alt={BRAND.homeCopy.heroImageAlt} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute bottom-8 left-8 right-8 lg:hidden text-white">
              <div className="text-[11px] uppercase tracking-[0.3em] opacity-60">Fundle · Édition 2026</div>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO STRIP — Fundle capabilities */}
      <section className="py-14 px-6 lg:px-12 max-w-[1400px] mx-auto border-b border-black/10">
        <div className="grid md:grid-cols-3 gap-12">
          <div className="flex gap-4">
            <Sparkles className="w-6 h-6 kazo-text-burgundy flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-display text-2xl mb-2">Loyalty · CRM · Rewards</h3>
              <p className="text-sm text-neutral-600 leading-relaxed">One engine across retail, malls, banks, hospitality, healthcare and airlines. Points, tiers, coupons, campaigns, wallet — production-grade.</p>
            </div>
          </div>
          <div className="flex gap-4">
            <Crown className="w-6 h-6 kazo-text-burgundy flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-display text-2xl mb-2">AI Customer Intelligence</h3>
              <p className="text-sm text-neutral-600 leading-relaxed">Fundle Brain — an AI analyst, strategist and campaign manager. Ask your customer data anything, in natural language, with live citations.</p>
            </div>
          </div>
          <div className="flex gap-4">
            <Gift className="w-6 h-6 kazo-text-burgundy flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-display text-2xl mb-2">Offline Attribution</h3>
              <p className="text-sm text-neutral-600 leading-relaxed">Bill-scan, POS integrations and identity-matching prove which campaigns drove which rupees — on the shop floor, not just in the dashboard.</p>
            </div>
          </div>
        </div>
      </section>

      {/* SIX CONNECTED PRODUCTS */}
      <section className="py-24 px-6 lg:px-12 max-w-[1400px] mx-auto">
        <div className="max-w-3xl mb-16">
          <div className="text-[11px] uppercase tracking-[0.3em] kazo-text-burgundy mb-4">THE ECOSYSTEM</div>
          <h2 className="editorial-headline text-5xl lg:text-7xl">Six connected<br /><em className="font-light">products.</em> One operating system.</h2>
          <p className="text-neutral-600 text-lg mt-6 max-w-2xl leading-relaxed">
            Fundle is not one product. It is an ecosystem — each module strong on its own, transformative when combined.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((p, i) => (
            <div key={p.name} className="border border-black/10 p-8 bg-white relative overflow-hidden fade-up hover:border-black/30 transition" style={{ animationDelay: `${i * 0.06}s` }} data-testid={`product-card-${p.n}`}>
              <div className="text-[11px] uppercase tracking-[0.3em] kazo-text-champagne mb-3">{p.n}</div>
              <div className="text-xs text-neutral-500 mb-2">{p.tag}</div>
              <h3 className="font-display text-2xl mb-3">{p.name}</h3>
              <p className="text-sm text-neutral-700 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* THESIS SPLIT — Real-World Activation */}
      <section className="kazo-bg-black text-white py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative aspect-[4/5] overflow-hidden">
            <img src={c.editorial_image_url || editorial} alt="Fundle real-world activation" className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] kazo-text-champagne mb-4">{c.editorial_eyebrow || "REAL-WORLD ACTIVATION"}</div>
            <h2 className="editorial-headline text-5xl lg:text-7xl mb-6">{c.editorial_headline || "Engagement while shopping — not just inside an app."}</h2>
            <p className="text-white/70 text-lg leading-relaxed mb-8 max-w-md">
              {c.editorial_body || "Fundle sits at the intersection of Loyalty · CRM · Retail Media · Analytics · AI · Rewards · WhatsApp Engagement · Offline Attribution — and connects consumers, brands, malls, advertisers, banks and partners into one intelligent engagement network."}
            </p>
            <Link to="/loyalty-benefits" className="k-btn kazo-bg-champagne text-black uppercase tracking-widest text-xs font-semibold" data-testid="cta-thesis-learn-more">
              Read the thesis <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* OUTCOMES */}
      <section className="py-24 px-6 lg:px-12 max-w-[1400px] mx-auto">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] kazo-text-burgundy mb-4">PROOF IN PRODUCTION</div>
            <h2 className="editorial-headline text-5xl lg:text-7xl mb-8">Outcomes,<br />on the operating<br /><em className="font-light">floor.</em></h2>
            <Link to="/how-it-works" className="editorial-link uppercase tracking-widest text-sm font-medium inline-flex items-center gap-2">See how it works <ArrowUpRight className="w-4 h-4" /></Link>
          </div>
          <div className="space-y-8">
            {[
              { step: "+38%", title: "Orchid Hotels · Hospitality", body: "Direct booking share up +38%. Single view of guest value across rooms, F&B and spa. OTA dependency dropped materially." },
              { step: "+45%", title: "Rangriti · Fashion", body: "Repeat rate up +45% in Q1. Fundle Brain took marketing ops from 6 people to 2." },
              { step: "-32%", title: "NewU Beauty · Beauty", body: "90-day churn down 32%. Churn predictions arrive 30 days before lapse. Win-back orchestration runs itself." },
            ].map((s) => (
              <div key={s.step} className="flex gap-8 border-b border-black/10 pb-8 last:border-0">
                <div className="font-display text-5xl kazo-text-champagne" style={{ fontWeight: 300 }}>{s.step}</div>
                <div>
                  <h3 className="font-display text-2xl mb-1">{s.title}</h3>
                  <p className="text-sm text-neutral-600 leading-relaxed max-w-md">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden" style={{ minHeight: 480 }}>
        <img src={c.boutique_image_url || boutique} alt="Fundle in production" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative max-w-3xl mx-auto px-6 py-24 text-center text-white">
          <div className="text-[11px] uppercase tracking-[0.3em] text-white/60 mb-6">GET STARTED</div>
          <h2 className="editorial-headline text-5xl lg:text-7xl mb-8">{c.final_cta_headline || "Plug into India's consumer engagement infrastructure."}</h2>
          <p className="text-white/80 mb-10 max-w-xl mx-auto">{c.final_cta_body || "A working session with the Fundle team — see how the six products combine for your specific brand, mall, bank or audience."}</p>
          <Link to="/contact" className="k-btn kazo-bg-champagne text-black uppercase tracking-widest text-xs font-semibold" data-testid="cta-final-join">
            Book a working session <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
